import styled from 'styled-components';
import { AccessibleHidden } from '../AccessibleHidden';
import Block from '@shared/components/ui/Block';
import type theme from '@shared/theme';
import { useControlField } from 'remix-validated-form';
import { useCallback } from 'react';

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
    name: string;
    className?: string;
    spacing?: keyof typeof theme.spacing;
    value: number | string | undefined;
    label: string;
    handleChange?: (value: any) => void;
};

function UnstyledOutlineRadioInput({
    name,
    value,
    label,
    spacing,
    className,
    ...props
}: Props) {
    const [selectedValue, setSelectedValue] = useControlField<
        number | string | undefined
    >(name);
    const checked = selectedValue === value;

    const handleChange = useCallback(() => {
        setSelectedValue(value);

        if (props.handleChange) {
            props.handleChange(value);
        }
    }, [props, setSelectedValue, value]);

    return (
        <Block spacing={spacing} className={className}>
            <AccessibleHidden
                id={`${name}-${label}`}
                type="radio"
                name={name}
                value={value}
                onChange={handleChange}
                checked={checked}
            />
            <Label htmlFor={`${name}-${label}`}>{label}</Label>
        </Block>
    );
}

const Label = styled.label`
    position: relative;
    display: flex;
    height: 100%;
    gap: 12px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    padding: 8px;
    border: solid 1px ${({ theme }) => theme.colors.uNavy20};

    &:hover {
        cursor: pointer;
    }
`;

export const OutlineRadioInput = styled(UnstyledOutlineRadioInput)`
    cursor: pointer;
    height: 100%;

    input:checked + label {
        background-color: ${({ theme }) => theme.colors.uNavy10};
        color: ${({ theme }) => theme.colors.uNavy};
    }
`;
